// Клиентский логгер admin SPA (см. PRD §14.4).
//
// * info/warn/error дублируются в console и копятся в очередь;
// * очередь батчем уходит на POST /api/v1/client-logs (rate-limited на сервере);
// * error/warn flush'ятся немедленно (с keepalive — переживают navigation);
// * сессионный request_id ставится в заголовок X-Request-ID для всех HTTP
//   через apiRequest — это склеивает фронтовую сессию с серверным журналом.
//
// Запреты — не логировать access_token, refresh_token, код подтверждения,
// magic-link, тело письма. Сервер на всякий случай маскирует ``token``/``code``
// в полях, но текст message клиента не парсится — следить тут.

export type ClientLogLevel = "info" | "warn" | "error";

interface ClientLogEvent {
  level: ClientLogLevel;
  event: string;
  message?: string;
  context?: Record<string, unknown>;
  request_id?: string;
  client_ts?: string;
}

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";
const SOURCE = "admin" as const;
const CLIENT_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? null;

const BATCH_MAX = 20;
const FLUSH_INTERVAL_MS = 5_000;

const queue: ClientLogEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

let currentRequestId: string | null = null;

function generateRequestId(): string {
  // crypto.randomUUID есть во всех таргетируемых браузерах (Vite ESM target).
  return crypto.randomUUID().replace(/-/g, "");
}

export function getRequestId(): string {
  if (currentRequestId === null) currentRequestId = generateRequestId();
  return currentRequestId;
}

/** Сбросить request_id (например, после logout/login). */
export function rotateRequestId(): void {
  currentRequestId = generateRequestId();
}

function enqueue(event: ClientLogEvent): void {
  queue.push({
    ...event,
    request_id: event.request_id ?? getRequestId(),
    client_ts: new Date().toISOString(),
  });
  if (event.level === "error" || queue.length >= BATCH_MAX) {
    void flushNow();
  } else if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      void flushNow();
    }, FLUSH_INTERVAL_MS);
  }
}

async function flushNow(): Promise<void> {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const events = queue.splice(0, BATCH_MAX);
  try {
    await fetch(`${API_BASE}/client-logs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source: SOURCE,
        client_version: CLIENT_VERSION,
        events,
      }),
      credentials: "include",
      keepalive: true,
    });
  } catch {
    // Сетевые ошибки логирования игнорируем — иначе зацикливание.
  }
}

function emit(level: ClientLogLevel, event: string, message?: string,
              context?: Record<string, unknown>): void {
  const consoleFn =
    level === "error" ? console.error
      : level === "warn" ? console.warn
        : console.info;
  consoleFn(`[${event}]`, message ?? "", context ?? {});
  enqueue({ level, event, message, context });
}

export const log = {
  info(event: string, message?: string, context?: Record<string, unknown>): void {
    emit("info", event, message, context);
  },
  warn(event: string, message?: string, context?: Record<string, unknown>): void {
    emit("warn", event, message, context);
  },
  error(event: string, message?: string, context?: Record<string, unknown>): void {
    emit("error", event, message, context);
  },
};

export function installGlobalErrorHandlers(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e: ErrorEvent) => {
    log.error("uncaught_error", e.message, {
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  });
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    log.error("unhandled_rejection", message);
  });
  // beforeunload — последний шанс дослать накопленное.
  window.addEventListener("beforeunload", () => {
    void flushNow();
  });
}
